import React, {createContext, ReactNode, useContext, useState} from "react";
import {RIGHT_DRAWER_WIDTH} from "@/constants/styles";
import {MINI_DRAWER_WIDTH} from "@/config";
import {
    CHAT_MESSAGE_TYPE,
    Message, MessageConversationalResponse,
    MessageResponse,
    UI_ACTION_TYPE,
    UIAction,
    UICreateContentActionParams,
    UIFilterAction
} from "../types/chatTypes";
import {deleteConversation, sendConversationalMessage, startConversation} from "../queries/agentQueries";
import {useAgentExecutor} from "../hooks/ActionCoordinator";
import {CollectionClip} from "@/types/collectionTypes";
import {getSubRoot} from "@/utils/utils";
import router from "@/routes";
import {useClipActiveFilters} from "@/contexts/application/ClipActiveFilters";
import {useKeyMomentActiveFilters} from "@/contexts/application/KeyMomentActiveFilters";
import {usePageSettings} from "@/contexts/PageSettingsContext";

export interface ChatMessage extends Message {
    choices?: string[][];
    media?: CollectionClip[];
    actions?: UIAction[];
}

export interface FullscreenType {
    isFullscreenChat: boolean
    setIsFullscreenChat: (fc: boolean) => void
    widthChat : (open:boolean, fullscreen:boolean) => number
    conversationalMessages: ChatMessage[];
    agentConversationId: string | null;
    isThinkingConversational: boolean;
    activeChoices: string[][] | null;
    isServiceUnavailable: boolean; // <--- NUOVO STATO EXPORTATO
    sendConversationalChatMessage: (message: string) => Promise<void>;
    openAgentConversation: (openNew: false) => Promise<void>;
    closeConversation: () => void;
    executeDelayedMessagesActions : (id: string) => void;
    isOpen: boolean

}

export const FullscreenContext = createContext<FullscreenType | undefined>(undefined)

interface Props {
    children: ReactNode;
}

export const widthChat = (open : boolean, fullscreen : boolean) : number => {
    if(open) {
        if(fullscreen) {
            return window.innerWidth - MINI_DRAWER_WIDTH
        }
        else
            return RIGHT_DRAWER_WIDTH
    }
    else
        return 0
}

export const FullscreenProvider = ({ children }: Props) => {

    const [isFullscreenChat, setIsFullscreenChat] = useState(false)
    const [conversationalMessages, setConversationalMessages] = useState<ChatMessage[]>([]);
    const [agentConversationId, setAgentConversationId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isThinkingConversational, setIsThinkingConversational] = useState<boolean>(false);
    const [activeChoices, setActiveChoices] = useState<string[][] | null>(null);
    const [isServiceUnavailable, setIsServiceUnavailable] = useState<boolean>(false); // <--- NUOVO STATO LOCALE
    const { executeActions } = useAgentExecutor();

    const {setClipActiveFilters} = useClipActiveFilters();
    const {setKeyMomentActiveFilters} = useKeyMomentActiveFilters()
    const {setCollectionGridShowed} = usePageSettings()

    const addMessage = (type: CHAT_MESSAGE_TYPE, content: string, choices?: string[][], media?: CollectionClip[], actions?: UIAction[]) => {
        const newMessage: ChatMessage = {
            id: crypto.randomUUID(),
            type,
            content,
            timestamp: new Date(),
            choices,
            media, // Clips, Cards, ...
            actions
        };
        setConversationalMessages((prev) => [...prev, newMessage]);
    };


    const executeDelayedMessagesActions = (id: string) => {
        const msg = conversationalMessages.find(ms => ms.id === id)
        if(msg) {
            msg.actions?.forEach(a => {
                if (a.type === UI_ACTION_TYPE.CREATE_CONTENT) {
                    const basePath = getSubRoot(window.location.pathname)
                    const targetPath = `${basePath}/clips`
                    const actionParams = a.params as UICreateContentActionParams
                    setClipActiveFilters(actionParams.filter_by_str)
                    setCollectionGridShowed('clip')
                    router.navigate(targetPath);
                }
                if(a.type === UI_ACTION_TYPE.FILTER) {
                    const basePath = getSubRoot(window.location.pathname)
                    const actionParams = a.params as UIFilterAction
                    if (actionParams.resource_type === 'key_moment') {
                        const targetPath = `${basePath}/keymoments/library`
                        setKeyMomentActiveFilters(actionParams.filter_by_str)
                        router.navigate(targetPath);
                    }
                    if (actionParams.resource_type === 'logic_clip') {
                        const targetPath = `${basePath}/clips`
                        setClipActiveFilters(actionParams.filter_by_str)
                        setCollectionGridShowed('clip')
                        router.navigate(targetPath);
                    }
                }
            })
        }
    }

    const sendConversationalChatMessage = async (message: string) => {
        if (isServiceUnavailable) return;

        addMessage(CHAT_MESSAGE_TYPE.USER, message);
        setIsThinkingConversational(true);
        setActiveChoices(null);

        try {
            const response: MessageConversationalResponse = await sendConversationalMessage(agentConversationId as string, message);

            executeActions(response.actions)

            const hasChoices = response.choices && response.choices.length > 0;

            addMessage(CHAT_MESSAGE_TYPE.AGENT, response.reply, hasChoices ? response.choices : undefined, response.media?.length !== 0 ? response.media : undefined, response.actions);

            if (hasChoices) {
                setActiveChoices(response.choices);
            }
        } finally {
            setIsThinkingConversational(false);
        }
    };

    const openAgentConversation = async (openNew: boolean = false) => {
        setIsOpen(true);
        setIsServiceUnavailable(false);

        try {
            if (agentConversationId && openNew) {
                await closeConversation();
                const response = await startConversation();
                setAgentConversationId(response.conversation_id);
                addMessage(CHAT_MESSAGE_TYPE.AGENT, response.message);
            }
            if (!agentConversationId) {
                const response = await startConversation();
                setAgentConversationId(response.conversation_id);

                const hasChoices = Boolean(response.choices && response.choices.length > 0);

                addMessage(
                    CHAT_MESSAGE_TYPE.AGENT,
                    response.message,
                    hasChoices ? response.choices : undefined,
                    response.media && response.media.length > 0 ? response.media : undefined,
                    response.actions
                );

                if (hasChoices) {
                    setActiveChoices(response.choices);
                }
            }
        } catch (error: any) {
            console.error("Error opening conversation:", error);

            const status = error?.status || error?.response?.status;

            if (status === 503) {
                setIsServiceUnavailable(true);
                addMessage(
                    CHAT_MESSAGE_TYPE.AGENT,
                    "AI Service unavailable, contact the administrator"
                );
            }
        }
    };

    const closeConversation = async () => {
        try {
            if (agentConversationId) {
                await deleteConversation(agentConversationId as string);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsOpen(false);
            setConversationalMessages([]);
            setAgentConversationId(null);
            setActiveChoices(null);
            setIsServiceUnavailable(false);
        }
    };


    return (
        <FullscreenContext.Provider value={{ isFullscreenChat, setIsFullscreenChat, widthChat,
            conversationalMessages,
            agentConversationId,
            isOpen,
            isThinkingConversational,
            activeChoices,
            isServiceUnavailable,
            sendConversationalChatMessage,
            openAgentConversation,
            closeConversation,
            executeDelayedMessagesActions}}>
            {children}
        </FullscreenContext.Provider>
    );
}

export const useFullscreenChat = (): FullscreenType => {
    const context = useContext(FullscreenContext);
    if (context === undefined) {
        throw new Error('useFullscreenChat must be used within a FullscreenProvider');
    }
    return context;
};